import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IdValidation } from 'idmission-web-sdk';
import { memo, useCallback, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { ProgressDots } from '@/components/DataDisplay/Indicator/ProgressDots';
import { Icon } from '@/components/Icon/Icon';
import { IconCell } from '@/components/Icon/IconCell';
import { ErrorMessage } from '@/components/Idmission/ErrorMessage';
import { IdmButton } from '@/components/Idmission/IdmButton.styled';
import { IdmCard } from '@/components/Idmission/IdmCard.styled';
import {
  IdmClassNames,
  IdmStylesMobile,
} from '@/components/Idmission/Idmission.styled';
import { RejectedMessage } from '@/components/Idmission/RejectedMessage';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';
import { Screen } from '@/components/Layout/Screen';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';
import { useIdmissionVerbiage } from '@/hooks/verification/useIdmissionVerbiage';
import { useIdmissionVerification } from '@/hooks/verification/useIdmissionVerification';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectUserHolderJointMinor } from '@/redux/user-data';

import { PROGRESS_STEPS } from '@/utils/progressSteps';
import { renderHTMLText } from '@/utils/string';
import { isSurinameUser } from '@/utils/user';

import {
  CardWrapper,
  ContentWrapper,
} from './IdmissionIDValidationMobile.styled';

export const MinorIdmissionIDValidationMobile: React.FC = memo(() => {
  const holderJointMinor = useAppSelector(selectUserHolderJointMinor);
  const user = useAppSelector(selectLoggedInUser);
  const isSuriname = isSurinameUser(user);
  const minorClientId = holderJointMinor?.clientId;
  const [isVerificationStarted, setIsVerificationStarted] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslate();
  const { verbiage } = useIdmissionVerbiage();

  const onCompleted = () => {
    navigate(ROUTE.MINOR_BASIC_INFO, {
      replace: true,
    });
  };

  const {
    sessionId,
    error,
    isIDRejected,
    acknowledgeRejection,
    isProcessing,
    isCompleted,
    idCaptureRequirement,
    startVerification,
    handleComplete,
    handleAcknowledgeRejection,
    resetVerification,
  } = useIdmissionVerification({
    type: 'id',
    autoInit: false,
    minorClientId,
    initialAcknowledgeRejection: !!location.state?.skipRejection,
    onCompleted,
  });

  const handleStartVerification = useCallback(async () => {
    try {
      setIsVerificationStarted(true);
      setIsLoading(true);
      await startVerification();
    } catch (err) {
      console.error('Failed to start verification:', err);
    } finally {
      setIsLoading(false);
    }
  }, [startVerification]);

  const handleOnExit = useCallback(() => {
    resetVerification();
    setIsVerificationStarted(false);
  }, [resetVerification]);

  const renderVerificationContent = () => {
    if (!sessionId || isProcessing || isCompleted) {
      return (
        <FullPageLoaderOverlay
          bgColor={(theme: Theme) => theme.palette.background.paper}
        />
      );
    }

    if (error) {
      return <ErrorMessage error={error} onAcknowledge={handleOnExit} />;
    }

    return (
      <IdmStylesMobile>
        <IdValidation
          sessionId={sessionId}
          idCaptureRequirement={idCaptureRequirement}
          onComplete={handleComplete}
          onExitCapture={handleOnExit}
          allowUploadingDocumentsFromStorage={false}
          classNames={IdmClassNames.idCapture}
          verbiage={verbiage.idCapture}
        />
      </IdmStylesMobile>
    );
  };

  if (isIDRejected && !acknowledgeRejection) {
    return (
      <RejectedMessage
        isIDRejected={isIDRejected}
        isSelfieRejected={false}
        isLivenessRejected={false}
        isMinor
        onAcknowledge={handleAcknowledgeRejection}
      />
    );
  }

  if (isVerificationStarted) {
    return renderVerificationContent();
  }

  return (
    <Screen
      header={{
        centerSlot: (
          <ProgressDots
            {...PROGRESS_STEPS.livenessVerificationStep}
            activeColor={(theme: Theme) => theme.palette.primaries[400]}
            inactiveColor={(theme: Theme) => theme.palette.grey[300]}
          />
        ),
      }}
      bgColor={(theme: Theme) => theme.palette.grey[50]}
    >
      <ContentWrapper>
        <Typography variant="h3" textAlign="center">
          {t('verification.idmission.child.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {renderHTMLText(
            t(
              `verification.idmission.gettingStartedInstruction.${idCaptureRequirement}`,
            ),
          )}
        </Typography>

        {error && (
          <Alert
            icon={<Icon name="alert" />}
            color="error"
            variant="standard"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        )}

        <CardWrapper>
          <IdmCard>
            <IconCell
              name="camera"
              color={(theme: Theme) => theme.palette.common.white}
              bgColor={(theme: Theme) => theme.palette.primaries[500]}
              size={80}
              iconSize={40}
            />
            <IdmButton
              onClick={handleStartVerification}
              variant="contained"
              disabled={isLoading}
              size="large"
              fullWidth
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('verification.idmission.idCapture.captureWithCameraText')
              )}
            </IdmButton>
          </IdmCard>
        </CardWrapper>

        <Stack direction="column" gap={2} alignItems="stretch">
          <Typography variant="body2" color="text.secondary">
            {isSuriname ? (
              <Trans
                i18nKey="verification.verifyMinorID.suriname.footerText"
                components={{ bold: <strong /> }}
              />
            ) : (
              t('verification.verifyMinorID.footerText')
            )}
          </Typography>
          <Button
            variant="outlined"
            fullWidth
            component={Link}
            to={ROUTE.VERIFY_MINOR_BIRTH_CERTIFICATE}
            sx={{ borderRadius: 2 }}
          >
            {t('verification.verifyMinorID.verifyWithBirthCertificate')}
          </Button>
          {isSuriname && (
            <Button
              variant="outlined"
              fullWidth
              component={Link}
              to={ROUTE.VERIFY_MINOR_FAMILY_BOOK}
              sx={{ borderRadius: 2 }}
            >
              {t('verification.verifyMinorID.verifyWithFamilyBook')}
            </Button>
          )}
        </Stack>
      </ContentWrapper>
    </Screen>
  );
});
